<template>
  <div class="platform-auth-container">
    <div class="auth-card">
      <div class="auth-header">
        <div class="logo">
          <img src="@/assets/omind_logo.png" alt="OMIND" class="logo-img" />
        </div>
        <h1>Reset Password</h1>
        <p class="subtitle">Choose a new password for your platform account</p>
      </div>

      <div v-if="!hasValidLink" class="error-message">
        Invalid reset link. Missing email or reset key.
      </div>

      <form v-else class="auth-form" @submit.prevent="handleSubmit">
        <div class="form-group">
          <label for="email">Email</label>
          <input
            id="email"
            :value="email"
            type="email"
            data-testid="platform-reset-email"
            disabled
          />
        </div>

        <div class="form-group">
          <label for="password">New password</label>
          <input
            id="password"
            v-model="password"
            type="password"
            data-testid="platform-reset-password"
            placeholder="Minimum 8 characters"
            required
            minlength="8"
            :disabled="loading || done"
          />
        </div>

        <div class="form-group">
          <label for="confirm">Confirm password</label>
          <input
            id="confirm"
            v-model="confirmPassword"
            type="password"
            data-testid="platform-reset-confirm"
            placeholder="Confirm password"
            required
            minlength="8"
            :disabled="loading || done"
          />
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div v-if="success" class="success-message">
          {{ success }}
        </div>

        <button
          v-if="!done"
          type="submit"
          class="submit-btn"
          data-testid="platform-reset-submit"
          :disabled="loading"
        >
          <span v-if="loading">Resetting...</span>
          <span v-else>Reset password</span>
        </button>

        <router-link to="/platform/login" class="auth-link" data-testid="platform-reset-login">
          Go to login
        </router-link>
      </form>
    </div>
  </div>
</template>

<script>
import { platformApi } from '@/services/platformApi';

export default {
  name: 'PlatformResetPassword',
  data() {
    return {
      email: '',
      validationKey: '',
      password: '',
      confirmPassword: '',
      loading: false,
      error: null,
      success: null,
      done: false,
    };
  },
  computed: {
    hasValidLink() {
      return Boolean(this.email && this.validationKey);
    },
  },
  created() {
    const { email, key } = this.$route.query;
    this.email = email ? decodeURIComponent(email) : '';
    this.validationKey = key ? decodeURIComponent(key) : '';
  },
  methods: {
    async handleSubmit() {
      this.error = null;
      this.success = null;

      if (this.password !== this.confirmPassword) {
        this.error = 'Passwords do not match';
        return;
      }

      this.loading = true;
      try {
        const data = await platformApi.resetPassword({
          email: this.email,
          password: this.password,
          validationKey: this.validationKey,
        });
        this.success = data.message || 'Password reset successful. You can now log in.';
        this.done = true;
      } catch (err) {
        this.error = err.response?.data?.message || 'Could not reset password. The link may be invalid or expired.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.platform-auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f8f8 0%, #ffffff 50%, #f8f8f8 100%);
  padding: 20px;
  font-family: 'AsenPro', sans-serif;
}

.auth-card {
  background: #ffffff;
  border: 1px solid #e7e7e7;
  border-radius: 2rem;
  padding: 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
}

.auth-header {
  text-align: center;
  margin-bottom: 30px;
}

.logo {
  margin-bottom: 16px;
}

.logo-img {
  width: 160px;
  height: auto;
}

.auth-header h1 {
  color: #1E2019;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.subtitle {
  color: #666666;
  font-size: 14px;
  margin: 8px 0 0;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: #1E2019;
  font-size: 13px;
  font-weight: 500;
}

.form-group input {
  padding: 14px 16px;
  border: 1px solid #e7e7e7;
  border-radius: 0.75rem;
  background: #f8f8f8;
  color: #1E2019;
  font-size: 15px;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #e4821a;
  background: #ffffff;
}

.form-group input::placeholder {
  color: #999999;
}

.form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  background: rgba(228, 75, 85, 0.1);
  border: 1px solid rgba(228, 75, 85, 0.3);
  color: #e44b55;
  padding: 12px 16px;
  border-radius: 0.75rem;
  font-size: 13px;
  margin-bottom: 16px;
}

.success-message {
  background: rgba(174, 234, 183, 0.2);
  border: 1px solid rgba(174, 234, 183, 0.5);
  color: #2d8a3e;
  padding: 12px 16px;
  border-radius: 0.75rem;
  font-size: 13px;
}

.submit-btn {
  padding: 14px 24px;
  border: none;
  border-radius: 2rem;
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(228, 130, 26, 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-link {
  color: #e4821a;
  font-size: 13px;
  text-decoration: none;
  text-align: center;
}

.auth-link:hover {
  text-decoration: underline;
}
</style>
